import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, Plus, X, Loader2, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtD } from "@/lib/semanas";

const ESTADO = { enviado: "text-emerald-700 bg-emerald-50", error: "text-red-700 bg-red-50", omitida: "text-slate-600 bg-slate-100",
  pendiente: "text-amber-700 bg-amber-50", enviando: "text-sky-700 bg-sky-50" };

function Historial() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["cne", "job"], queryFn: async () => (await api.get("/control-semanal/cne/job/estado")).data });
  const retry = async (vig) => {
    try { await api.post("/control-semanal/cne/notificacion/reintentar", { vigencia_inicio: vig }); toast.success("Reintento ejecutado."); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo reintentar."); }
    qc.invalidateQueries({ queryKey: ["cne", "job"] });
  };
  const semanas = (data?.semanas || []).slice(0, 6);
  if (!semanas.length) return <p className="text-xs text-slate-400">Sin semanas procesadas por el job.</p>;
  return (
    <div className="space-y-1.5" data-testid="cne-notif-historial">
      {semanas.map((s) => (
        <div key={s.vigencia_inicio} className="flex items-center gap-2 text-xs rounded-lg border border-slate-100 px-3 py-2" data-testid={`cne-notif-semana-${s.vigencia_inicio}`}>
          <span className="font-semibold text-slate-800 w-24">{fmtD(s.vigencia_inicio)}</span>
          <span className="text-slate-500">Importación: <b className="text-slate-700">{s.estado}</b></span>
          <span className={`ml-auto rounded-full px-2 py-0.5 font-semibold ${ESTADO[s.notif_estado] || "text-slate-400"}`} title={s.notif_detalle || ""}>
            Correo: {s.notif_estado || "—"}
          </span>
          {s.notif_estado === "error" && (
            <button onClick={() => retry(s.vigencia_inicio)} className="text-red-600 hover:text-red-700" data-testid={`cne-notif-retry-${s.vigencia_inicio}`}><RotateCw className="w-3.5 h-3.5" /></button>
          )}
        </div>
      ))}
      {semanas.filter((s) => ["error", "omitida"].includes(s.notif_estado) && s.notif_detalle).slice(0, 2).map((s) => (
        <p key={`d-${s.vigencia_inicio}`} className="text-[11px] text-slate-500" data-testid={`cne-notif-detalle-${s.vigencia_inicio}`}>{fmtD(s.vigencia_inicio)}: {s.notif_detalle}</p>
      ))}
    </div>
  );
}

export function CneNotificacion({ tipo = "cne" }) {
  const base = `/control-semanal/${tipo}`;
  const pemex = tipo === "pemex";
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [nuevo, setNuevo] = useState("");
  const [saving, setSaving] = useState(false);
  const [probando, setProbando] = useState(false);
  const { data } = useQuery({ queryKey: [tipo, "notif"], enabled: open, queryFn: async () => (await api.get(`${base}/notificacion/config`)).data });
  useEffect(() => { if (data) { setCfg(data); } }, [data]);
  const add = () => {
    const v = nuevo.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { toast.error("Escribe un correo válido."); return; }
    setCfg((c) => ({ ...c, destinatarios: [...new Set([...c.destinatarios, v])] }));
    setNuevo("");
  };
  const save = async () => {
    setSaving(true);
    try { setCfg((await api.put(`${base}/notificacion/config`, cfg)).data); toast.success("Notificación guardada."); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo guardar."); }
    finally { setSaving(false); }
  };
  const prueba = async () => {
    setProbando(true);
    try { toast.success(`Correo: ${(await api.post(`${base}/notificacion/enviar`)).data.resultado}`); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo enviar el correo."); }
    finally { setProbando(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="cne-notif-btn" className="h-10 px-4 rounded-xl text-sm font-semibold flex items-center gap-2 border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors">
        <Mail className="w-4 h-4" />Notificación por correo
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white text-slate-900" data-testid="cne-notif-dialog">
          <DialogHeader><DialogTitle>Notificación automática de precios {pemex ? "PEMEX" : "CNE"}</DialogTitle></DialogHeader>
          {!cfg ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
            <div className="space-y-4">
              <label className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
                <span className="text-sm text-slate-700">{pemex ? "Enviar correo al importar una nueva circular (job de domingos o importación manual)" : "Enviar correo al importar la nueva semana"}</span>
                <Switch checked={cfg.activo} onCheckedChange={(v) => setCfg({ ...cfg, activo: v })} data-testid="cne-notif-activo" />
              </label>
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Destinatarios</p>
                {cfg.destinatarios.map((d) => (
                  <div key={d} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-sm" data-testid={`cne-notif-dest-${d}`}>
                    {d}<button onClick={() => setCfg({ ...cfg, destinatarios: cfg.destinatarios.filter((x) => x !== d) })} className="text-slate-400 hover:text-red-600" data-testid={`cne-notif-remove-${d}`}><X className="w-4 h-4" /></button>
                  </div>
                ))}
                {!cfg.destinatarios.length && <p className="text-xs text-amber-700" data-testid="cne-notif-sin-dest">Sin destinatarios: no se enviará ningún correo.</p>}
                <div className="flex gap-2">
                  <input value={nuevo} onChange={(e) => setNuevo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="correo@tomza.com" data-testid="cne-notif-input"
                    className="h-9 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
                  <button onClick={add} data-testid="cne-notif-add" className="h-9 px-3 rounded-lg border border-slate-200 text-sm flex items-center gap-1 hover:bg-slate-50"><Plus className="w-4 h-4" />Agregar</button>
                </div>
              </div>
              <button onClick={save} disabled={saving} data-testid="cne-notif-save" className="w-full h-10 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
                {saving ? "Guardando…" : "Guardar configuración"}
              </button>
              {pemex && <button onClick={prueba} disabled={probando || !cfg.destinatarios.length} data-testid="pemex-notif-enviar" className="w-full h-10 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                {probando ? "Enviando…" : "Enviar correo de la última circular"}
              </button>}
              {!pemex && <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Últimas semanas del job</p>
                <Historial />
              </div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
